import { type ReactElement, useId } from 'react';
import { Box, EmptyState, Panel, Stack } from '@/ui';
import {
  Button,
  IconButton,
  InputAdornment,
  TextField,
  VisibilityIcon,
  VisibilityOffIcon,
} from '@/ui/primitives';
import { passwordIconButtonSx } from './userSettingsStyles';
import type { PasswordChangeFormState } from '@/features/users/hooks/usePasswordChangeForm';

type PasswordChangeFormProps = { password: PasswordChangeFormState };

export const PasswordChangeForm = ({ password }: PasswordChangeFormProps): ReactElement => {
  const currentPasswordId = useId();
  const newPasswordId = useId();
  const confirmPasswordId = useId();

  return (
    <Panel
      title="Change Password"
      actions={
        <Button
          variant="contained"
          color="primary"
          disableElevation
          fullWidthOnMobile={false}
          disabled={password.saveDisabled}
          aria-label="Change Password"
          onClick={password.onSave}
        >
          Change Password
        </Button>
      }
    >
      <Stack gap="md">
        <Box>
          <TextField
            id={currentPasswordId}
            label="Current Password"
            type={password.showCurrentPassword ? 'text' : 'password'}
            value={password.currentPassword}
            onChange={(event) => {
              password.onCurrentPasswordChange(event.target.value);
            }}
            fullWidth
            disabled={password.saving}
            error={password.currentPasswordError}
            helperText={password.currentPasswordHelperText}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={
                        password.showCurrentPassword
                          ? 'Hide current password'
                          : 'Show current password'
                      }
                      edge="end"
                      onClick={password.onToggleCurrentPassword}
                      sx={passwordIconButtonSx}
                    >
                      {password.showCurrentPassword ? (
                        <VisibilityOffIcon fontSize="small" />
                      ) : (
                        <VisibilityIcon fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>
        <Box>
          <TextField
            id={newPasswordId}
            label="New Password"
            type={password.showNewPassword ? 'text' : 'password'}
            fullWidth
            value={password.newPassword}
            onChange={(event) => {
              password.onNewPasswordChange(event.target.value);
            }}
            disabled={password.saving}
            error={password.newPasswordError}
            helperText={password.newPasswordHelperText}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={
                        password.showNewPassword ? 'Hide new password' : 'Show new password'
                      }
                      edge="end"
                      onClick={password.onToggleNewPassword}
                      sx={passwordIconButtonSx}
                    >
                      {password.showNewPassword ? (
                        <VisibilityOffIcon fontSize="small" />
                      ) : (
                        <VisibilityIcon fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>
        <Box>
          <TextField
            id={confirmPasswordId}
            label="Confirm New Password"
            type={password.showConfirmPassword ? 'text' : 'password'}
            fullWidth
            value={password.confirmPassword}
            onChange={(event) => {
              password.onConfirmPasswordChange(event.target.value);
            }}
            disabled={password.saving}
            error={password.confirmPasswordError}
            helperText={password.confirmPasswordHelperText}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={
                        password.showConfirmPassword
                          ? 'Hide confirm password'
                          : 'Show confirm password'
                      }
                      edge="end"
                      onClick={password.onToggleConfirmPassword}
                      sx={passwordIconButtonSx}
                    >
                      {password.showConfirmPassword ? (
                        <VisibilityOffIcon fontSize="small" />
                      ) : (
                        <VisibilityIcon fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>
        {password.successMessage ? (
          <EmptyState title={password.successMessage} surface={false} />
        ) : null}
      </Stack>
    </Panel>
  );
};
